import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

export const EMPTY_FILTERS = { activity: [], content: [], popularity: null, membership: [], categories: [] };

export const GROUPS = [
  { key: "activity", title: "Activity", options: [["live", "Currently Live"], ["active", "Recently Active"], ["joined", "Recently Joined"]] },
  { key: "content", title: "Content", options: [["photos", "Photos"], ["videos", "Videos"], ["streaming", "Live Streaming"], ["ppv", "Pay-Per-View Available"]] },
  { key: "popularity", title: "Popularity", single: true, options: [["followed", "Most Followed"], ["trending", "Trending Creators"], ["updated", "Most Recently Updated"]] },
  { key: "membership", title: "Membership", options: [["free", "Free Public Previews Available"], ["paid", "Paid Membership Available"], ["following", "Creators I Follow"], ["subscribed", "Creators I'm Subscribed To"]] },
];

const TEST = {
  live: (c) => !!c.live, active: (c) => c.recently_active, joined: (c) => c.is_new,
  photos: (c) => c.has_photos, videos: (c) => c.has_videos, streaming: (c) => c.streams_live, ppv: (c) => c.has_ppv,
  free: (c) => c.has_free_previews, paid: (c) => c.paid_membership, following: (c) => c.following, subscribed: (c) => c.subscribed,
};
const SORT = {
  followed: (a, b) => b.follower_count - a.follower_count,
  trending: (a, b) => b.net_followers_7d - a.net_followers_7d,
  updated: (a, b) => (b.last_updated_at || "").localeCompare(a.last_updated_at || ""),
};

export function filterCreators(rows, f) {
  let out = rows.filter((c) => [...f.activity, ...f.content, ...f.membership].every((k) => TEST[k](c)));
  if (f.categories.length) out = out.filter((c) => (c.categories || []).some((x) => f.categories.includes(x)));
  if (f.popularity === "trending") out = out.filter((c) => c.net_followers_7d > 0);
  if (f.popularity) out = [...out].sort(SORT[f.popularity]);
  return out;
}

export function activeChips(f) {
  const label = Object.fromEntries(GROUPS.flatMap((g) => g.options));
  return [
    ...["activity", "content", "membership"].flatMap((g) => f[g].map((k) => ({ group: g, key: k, label: label[k] }))),
    ...(f.popularity ? [{ group: "popularity", key: f.popularity, label: label[f.popularity] }] : []),
    ...f.categories.map((k) => ({ group: "categories", key: k, label: k })),
  ];
}

export function removeChip(f, chip) {
  if (chip.group === "popularity") return { ...f, popularity: null };
  return { ...f, [chip.group]: f[chip.group].filter((k) => k !== chip.key) };
}

function Option({ on, label, onClick, testid, radio }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={on}
      className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors ${on ? "border-[#a03cff] bg-[#3a0d6e]/60 text-white shadow-[0_0_10px_rgba(160,60,255,0.35)]" : "border-white/10 bg-[#111015] text-white/85 hover:border-white/30"}`}>
      <span className={`flex h-4 w-4 shrink-0 items-center justify-center border ${radio ? "rounded-full" : "rounded"} ${on ? "border-[#c77dff] bg-[#8a2be2]" : "border-white/30"}`}>{on && <span className={`h-1.5 w-1.5 bg-white ${radio ? "rounded-full" : "rounded-sm"}`} />}</span>
      {label}
    </button>
  );
}

function useIsMobile() {
  const [m, setM] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const on = () => setM(window.innerWidth < 768);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return m;
}

export function FilterDrawer({ open, onOpenChange, value, onApply, categories, preview }) {
  const [draft, setDraft] = useState(value);
  const mobile = useIsMobile();
  useEffect(() => { if (open) setDraft(value); }, [open, value]);
  const toggle = (g, k, single) => setDraft((d) => (single ? { ...d, [g]: d[g] === k ? null : k } : { ...d, [g]: d[g].includes(k) ? d[g].filter((x) => x !== k) : [...d[g], k] }));
  const count = preview(draft);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={mobile ? "bottom" : "right"} data-testid="filter-drawer"
        className={`flex flex-col gap-0 border-[#3b1a5c] bg-[#0a070e] p-0 text-white ${mobile ? "max-h-[88vh] rounded-t-3xl" : "w-full sm:max-w-md"}`}>
        <SheetHeader className="border-b border-white/10 px-5 pb-4 pt-5 text-left">
          <SheetTitle className="text-white">Filters</SheetTitle>
          <SheetDescription className="text-xs text-[#a7a0af]">Combine filters to narrow creators. Everything is based on real PEEPING data.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4">
          {GROUPS.map((g) => (
            <section key={g.key} className="mb-5">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#c77dff]">{g.title}{g.single && <span className="ml-2 normal-case tracking-normal text-[#6f6878]">(sort · pick one)</span>}</h3>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {g.options.map(([k, label]) => (
                  <Option key={k} radio={g.single} label={label} testid={`fopt-${g.key}-${k}`}
                    on={g.single ? draft[g.key] === k : draft[g.key].includes(k)} onClick={() => toggle(g.key, k, g.single)} />
                ))}
              </div>
            </section>
          ))}
          <section className="mb-2">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#c77dff]">Creator Categories</h3>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((k) => <Option key={k} label={k} testid={`fopt-cat-${k.toLowerCase().replace(/\s/g, "-")}`} on={draft.categories.includes(k)} onClick={() => toggle("categories", k)} />)}
            </div>
          </section>
        </div>
        <div className="flex gap-3 border-t border-white/10 px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4">
          <button type="button" onClick={() => setDraft(EMPTY_FILTERS)} data-testid="filters-clear" className="flex-1 rounded-full border border-white/15 py-3 text-sm">Clear Filters</button>
          <button type="button" onClick={() => { onApply(draft); onOpenChange(false); }} data-testid="filters-apply"
            className="flex-[1.4] rounded-full border border-[#8a2be2] bg-gradient-to-b from-[#7a22d6] to-[#4a0f8c] py-3 text-sm font-semibold">Apply Filters · {count}</button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function ActiveChips({ chips, onRemove, onClear }) {
  if (!chips.length) return null;
  return (
    <div data-testid="active-filters" className="mb-3 flex flex-wrap items-center gap-2">
      {chips.map((c) => (
        <button key={`${c.group}-${c.key}`} onClick={() => onRemove(c)} data-testid={`active-chip-${c.key.toLowerCase().replace(/\s/g, "-")}`}
          className="flex items-center gap-1 rounded-full border border-[#a03cff]/60 bg-[#3a0d6e]/50 px-3 py-1 text-xs">{c.label} <X size={12} /></button>
      ))}
      <button onClick={onClear} data-testid="active-clear" className="text-xs text-[#c77dff] underline">Clear all</button>
    </div>
  );
}
